/**
 * PDF Page Viewer — renders all PDF pages vertically stacked with per-page width fitting.
 * Supports zoom (Ctrl+scroll, Ctrl+/-, keyboard), lazy rendering, and page navigation.
 *
 * Architecture:
 *   container
 *     ├ navBar (fixed UI, never scales)
 *     └ scrollArea
 *         └ page1Canvas, page2Canvas, ... (each scaled independently)
 *
 * Usage:
 *   PdfPageViewer.init(containerElement, pdfUrl, { page: 1 })
 */
window.PdfPageViewer = (function () {
  "use strict";

  var PDFJS_CDN = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  var PDFJS_WORKER_CDN = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  var PAGE_GAP = 40;
  var RENDER_BUFFER = 1; // render pages ±1 from visible range

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      // Reuse an in-flight tag instead of resolving immediately: the script may exist in the DOM
      // but still be downloading from a sibling init() call.
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === "true") {
          resolve();
        } else {
          existing.addEventListener("load", function () { resolve(); });
          existing.addEventListener("error", reject);
        }
        return;
      }
      var s = document.createElement("script");
      s.src = src;
      s.onload = function () { s.dataset.loaded = "true"; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // Cached so concurrent ensurePdfjs() callers (e.g. compare_eswppp's two viewers) await the same load.
  var _pdfjsLoadPromise = null;

  function ensurePdfjs() {
    if (window.pdfjsLib) return Promise.resolve();
    if (_pdfjsLoadPromise) return _pdfjsLoadPromise;
    _pdfjsLoadPromise = loadScript(PDFJS_CDN).then(function () {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
      }
    }).catch(function (err) {
      // Clear cache on failure so a manual retry can re-attempt the CDN fetch.
      _pdfjsLoadPromise = null;
      throw err;
    });
    return _pdfjsLoadPromise;
  }

  function withCacheBuster(url) {
    var separator = url.indexOf("?") >= 0 ? "&" : "?";
    return url + separator + "_cb=" + Date.now();
  }

  // Persistent, content-addressed PDF cache keyed by Active Storage blob key.
  // A content change yields a new blob key, so changed documents miss and refetch
  // (the cache-busting); unchanged ones (e.g. the master upload) hit. Falls back
  // to the network wherever the Cache API is absent.
  //
  // Origin Cache Storage is shared and byte-capped (Chrome QuotaExceededError).
  // Count-only pruning is not enough for large assembled PDFs, so writes evict
  // oldest entries until the put fits (or the cache is empty and we skip).
  var PDF_CACHE_NAME = "erx-pdf-v1";
  var PDF_CACHE_MAX_ENTRIES = 8;
  var PDF_CACHE_LEGACY_IDB = "erx-pdf-cache";

  // Range + cache-buster path. Matches pdf_editor.js: avoids Chrome cache bugs
  // on proxied PDF range requests.
  function loadPdfFromUrl(pdfUrl) {
    return ensurePdfjs().then(function () {
      return window.pdfjsLib.getDocument({
        url: withCacheBuster(pdfUrl),
        withCredentials: false,
        httpHeaders: { "Cache-Control": "no-cache" },
      }).promise;
    });
  }

  function loadPdfFromBytes(bytes) {
    return ensurePdfjs().then(function () {
      return window.pdfjsLib.getDocument({ data: bytes }).promise;
    });
  }

  function isQuotaError(err) {
    if (!err) return false;
    if (err.name === "QuotaExceededError" || err.code === 22) return true;
    return !!(err.message && /quota/i.test(err.message));
  }

  // Drop the oldest entries; Cache API preserves insertion order. maxEntries
  // defaults to the cap, but callers can leave room for an incoming entry.
  function prunePdfCache(cache, maxEntries) {
    var limit = typeof maxEntries === "number" ? maxEntries : PDF_CACHE_MAX_ENTRIES;
    return cache.keys().then(function (keys) {
      return Promise.all(
        keys.slice(0, Math.max(0, keys.length - limit))
          .map(function (k) { return cache.delete(k); })
      );
    });
  }

  // cache.put() consumes the body, so clone per attempt. On quota errors, delete
  // the oldest entry and retry until the write fits or nothing remains to evict.
  function putEvictingOnQuota(cache, request, response) {
    return cache.put(request, response.clone()).catch(function (err) {
      if (!isQuotaError(err)) throw err;
      return cache.keys().then(function (keys) {
        if (!keys.length) throw err;
        return cache.delete(keys[0]).then(function () {
          return putEvictingOnQuota(cache, request, response);
        });
      });
    });
  }

  // Persistent storage raises the Cache quota so large PDFs are not rejected/evicted.
  // Returns a promise so writers can wait for the (best-effort) grant before put.
  var persistStoragePromise = null;
  function requestPersistentStorage() {
    if (persistStoragePromise) return persistStoragePromise;
    if (navigator.storage && navigator.storage.persist) {
      persistStoragePromise = navigator.storage.persist().catch(function () { return false; });
    } else {
      persistStoragePromise = Promise.resolve(false);
    }
    return persistStoragePromise;
  }

  // Older IndexedDB cache and renamed Cache API buckets still occupy origin quota.
  var staleCachesReclaimed = false;
  function reclaimStalePdfCaches() {
    if (staleCachesReclaimed) return;
    staleCachesReclaimed = true;
    try {
      if (typeof indexedDB !== "undefined" && indexedDB.deleteDatabase) {
        indexedDB.deleteDatabase(PDF_CACHE_LEGACY_IDB);
      }
    } catch (e) { /* ignore */ }
    if (typeof caches !== "undefined" && caches.keys) {
      caches.keys().then(function (names) {
        return Promise.all(names.map(function (name) {
          if (name.indexOf("erx-pdf-") === 0 && name !== PDF_CACHE_NAME) {
            return caches.delete(name);
          }
        }));
      }).catch(function () {});
    }
  }

  // Serialize writes: compare-view mounts two PDFs at once, and Turbolinks can
  // re-init before the first put finishes. Parallel puts race the same quota.
  var pdfCacheWriteChain = Promise.resolve();

  function writePdfCacheEntry(cache, request, blob) {
    var toStore = new Response(blob, {
      status: 200,
      headers: { "Content-Type": "application/pdf" }
    });
    return requestPersistentStorage()
      .then(function () { return prunePdfCache(cache, PDF_CACHE_MAX_ENTRIES - 1); })
      .then(function () { return putEvictingOnQuota(cache, request, toStore); });
  }

  function cachePdfResponse(cache, request, response) {
    var job = response.blob().then(function (blob) {
      var run = pdfCacheWriteChain.then(function () {
        return writePdfCacheEntry(cache, request, blob);
      });
      pdfCacheWriteChain = run.catch(function () {});
      return run;
    });
    return job.catch(function (err) {
      console.warn("[PdfPageViewer] PDF cache write failed (large file/quota?):", err && err.message);
    });
  }

  function loadPdfDocument(pdfUrl, opts) {
    var cacheKey = opts && opts.cacheKey;
    if (!cacheKey || typeof caches === "undefined") return loadPdfFromUrl(pdfUrl);

    reclaimStalePdfCaches();
    requestPersistentStorage();
    var request = "/__pdf-cache__/" + encodeURIComponent(cacheKey);
    return caches.open(PDF_CACHE_NAME).then(function (cache) {
      return cache.match(request).then(function (hit) {
        if (hit) return hit.arrayBuffer().then(loadPdfFromBytes);
        return fetch(pdfUrl, { credentials: "same-origin" }).then(function (resp) {
          if (!resp.ok) throw new Error("PDF fetch failed: " + resp.status);
          // Store in the background so a large PDF never delays first render.
          cachePdfResponse(cache, request, resp.clone());
          return resp.arrayBuffer().then(loadPdfFromBytes);
        });
      });
    }).catch(function () { return loadPdfFromUrl(pdfUrl); });  // never break viewing
  }

  function init(containerEl, pdfUrl, opts) {
    opts = opts || {};
    var initialPage = parseInt(opts.page, 10) || 1;
    var onPageChange = opts.onPageChange || null;

    // Optional allowlist: when present, only these (real) page numbers are shown,
    // while nav/labels keep displaying the real page numbers.
    var pageList = null;
    if (opts.pages && opts.pages.length) {
      pageList = opts.pages.map(function (n) { return parseInt(n, 10); })
        .filter(function (n) { return n > 0; });
      pageList = pageList.filter(function (n, i) { return pageList.indexOf(n) === i; })
        .sort(function (a, b) { return a - b; });
      if (pageList.length === 0) pageList = null;
    }

    containerEl.innerHTML = "";
    containerEl.style.position = "relative";

    var state = {
      pdfDoc: null,
      totalPages: 0,
      userZoom: 1.0,
      pageWrappers: [],   // DOM elements, indexed by display position
      renderedPages: {},  // real page number -> true if rendered
      renderingPages: {}, // real page number -> true if currently rendering
      containerEl: containerEl,
      pdfUrl: pdfUrl,
      cacheKey: opts.cacheKey || null,  // when set, load bytes from the persistent cache
      pageList: pageList,  // null = show all pages
      currentDisplayIdx: 0,
      currentPage: initialPage
    };

    // Map between display positions and real PDF page numbers.
    function displayCount() { return state.pageList ? state.pageList.length : state.totalPages; }
    function realPageAt(displayIdx) { return state.pageList ? state.pageList[displayIdx] : (displayIdx + 1); }
    function displayIdxOfReal(realNum) { return state.pageList ? state.pageList.indexOf(realNum) : (realNum - 1); }

    // --- Build UI ---
    var navBar = document.createElement("div");
    navBar.style.cssText = "display:flex; align-items:center; justify-content:center; gap:8px; padding:8px 12px; background:#fff; border:1px solid #dee2e6; border-radius:4px; margin-bottom:8px; flex-wrap:wrap; position:sticky; top:0; z-index:5;";

    var prevBtn = document.createElement("button");
    prevBtn.className = "btn btn-sm btn-outline-secondary";
    prevBtn.innerHTML = '<i class="fas fa-chevron-left"></i>';
    prevBtn.title = "Previous page";
    prevBtn.onclick = function () { goToDisplay(state.currentDisplayIdx - 1); };

    var nextBtn = document.createElement("button");
    nextBtn.className = "btn btn-sm btn-outline-secondary";
    nextBtn.innerHTML = '<i class="fas fa-chevron-right"></i>';
    nextBtn.title = "Next page";
    nextBtn.onclick = function () { goToDisplay(state.currentDisplayIdx + 1); };

    var pageInput = document.createElement("input");
    pageInput.type = "number";
    pageInput.min = "1";
    pageInput.style.cssText = "width:55px; text-align:center;";
    pageInput.className = "form-control form-control-sm";
    pageInput.value = initialPage;
    pageInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        goToPage(parseInt(pageInput.value, 10));
      }
    });

    if (state.pageList) {
      pageInput.title = "Showing only changed page(s): " + state.pageList.join(", ");
    }

    var pageTotal = document.createElement("span");
    pageTotal.style.cssText = "font-size:0.85rem; color:#555;";
    pageTotal.textContent = "/ ...";

    var zoomOutBtn = document.createElement("button");
    zoomOutBtn.className = "btn btn-sm btn-outline-secondary";
    zoomOutBtn.innerHTML = '<i class="fas fa-search-minus"></i>';
    zoomOutBtn.title = "Zoom out (Ctrl -)";
    zoomOutBtn.onclick = function () { adjustZoom(-0.15); };

    var zoomLabel = document.createElement("span");
    zoomLabel.style.cssText = "font-size:0.8rem; min-width:45px; text-align:center; color:#555;";
    zoomLabel.textContent = "100%";

    var zoomInBtn = document.createElement("button");
    zoomInBtn.className = "btn btn-sm btn-outline-secondary";
    zoomInBtn.innerHTML = '<i class="fas fa-search-plus"></i>';
    zoomInBtn.title = "Zoom in (Ctrl +)";
    zoomInBtn.onclick = function () { adjustZoom(0.15); };

    var zoomResetBtn = document.createElement("button");
    zoomResetBtn.className = "btn btn-sm btn-outline-secondary";
    zoomResetBtn.innerHTML = '<i class="fas fa-compress-arrows-alt"></i>';
    zoomResetBtn.title = "Reset zoom";
    zoomResetBtn.onclick = function () { state.userZoom = 1.0; rerenderAll(); };

    var sep = function () {
      var d = document.createElement("div");
      d.style.cssText = "width:1px; height:20px; background:#dee2e6; margin:0 4px;";
      return d;
    };

    navBar.appendChild(prevBtn);
    navBar.appendChild(pageInput);
    navBar.appendChild(pageTotal);
    navBar.appendChild(nextBtn);
    navBar.appendChild(sep());
    navBar.appendChild(zoomOutBtn);
    navBar.appendChild(zoomLabel);
    navBar.appendChild(zoomInBtn);
    navBar.appendChild(zoomResetBtn);

    var scrollArea = document.createElement("div");
    scrollArea.style.cssText = "width:100%; overflow-y:auto; background:#e8e8e8; padding:20px 0;";
    scrollArea.id = "pdf-viewer-scroll-area";

    containerEl.appendChild(navBar);
    containerEl.appendChild(scrollArea);

    var loadingMsg = document.createElement("div");
    loadingMsg.style.cssText = "text-align:center; padding:60px 20px; color:#666;";
    loadingMsg.innerHTML = '<i class="fas fa-spinner fa-spin fa-2x"></i><div style="margin-top:12px;">Loading PDF...</div>';
    scrollArea.appendChild(loadingMsg);

    // --- Core functions ---

    function adjustZoom(delta) {
      state.userZoom = Math.min(Math.max(state.userZoom + delta, 0.25), 5.0);
      rerenderAll();
    }

    function updateZoomLabel() {
      zoomLabel.textContent = Math.round(state.userZoom * 100) + "%";
    }

    function getBaseScale(pageRawWidth) {
      var containerWidth = scrollArea.clientWidth - 40;
      if (containerWidth <= 0) containerWidth = 600;
      return containerWidth / pageRawWidth;
    }

    // Scroll to a display position (index into the shown page list).
    function goToDisplay(displayIdx) {
      if (!state.pdfDoc) return;
      displayIdx = Math.max(0, Math.min(displayIdx, displayCount() - 1));
      state.currentDisplayIdx = displayIdx;
      var realNum = realPageAt(displayIdx);
      state.currentPage = realNum;
      pageInput.value = realNum;
      var wrapper = state.pageWrappers[displayIdx];
      if (wrapper) {
        wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      if (onPageChange) onPageChange(realNum);
    }

    // Navigate by real page number (ignored if not in the shown list).
    function goToPage(realNum) {
      var displayIdx = displayIdxOfReal(realNum);
      if (displayIdx < 0) return;
      goToDisplay(displayIdx);
    }

    function createPagePlaceholders() {
      scrollArea.innerHTML = "";
      state.pageWrappers = [];
      state.renderedPages = {};
      state.renderingPages = {};

      for (var i = 0; i < displayCount(); i++) {
        var realNum = realPageAt(i);
        var pw = document.createElement("div");
        pw.className = "pdf-page-wrapper";
        pw.dataset.pageNum = realNum;
        pw.style.cssText = "display:flex; justify-content:center; margin-bottom:" + PAGE_GAP + "px; min-height:200px; position:relative;";

        var placeholder = document.createElement("div");
        placeholder.style.cssText = "color:#999; font-size:0.85rem; position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);";
        placeholder.textContent = "Page " + realNum;
        pw.appendChild(placeholder);

        scrollArea.appendChild(pw);
        state.pageWrappers.push(pw);
      }
    }

    function renderPage(pageNum) {
      if (state.renderedPages[pageNum] || state.renderingPages[pageNum]) return;
      if (!state.pdfDoc) return;
      var displayIdx = displayIdxOfReal(pageNum);
      if (displayIdx < 0) return;
      state.renderingPages[pageNum] = true;

      state.pdfDoc.getPage(pageNum).then(function (page) {
        var rawVp = page.getViewport({ scale: 1 });
        var baseScale = getBaseScale(rawVp.width);
        var finalScale = baseScale * state.userZoom;
        finalScale = Math.min(Math.max(finalScale, 0.1), 10.0);
        var viewport = page.getViewport({ scale: finalScale });

        var wrapper = state.pageWrappers[displayIdx];
        if (!wrapper) { state.renderingPages[pageNum] = false; return; }

        wrapper.innerHTML = "";
        wrapper.style.minHeight = viewport.height + "px";

        var canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.cssText = "display:block; box-shadow:0 2px 8px rgba(0,0,0,0.18); background:#fff;";
        canvas.dataset.pageNum = pageNum;
        wrapper.appendChild(canvas);

        var ctx = canvas.getContext("2d");
        page.render({ canvasContext: ctx, viewport: viewport }).promise.then(function () {
          state.renderedPages[pageNum] = true;
          state.renderingPages[pageNum] = false;
        }).catch(function (err) {
          state.renderingPages[pageNum] = false;
          var name = err && err.name;
          if (name === "RenderingCancelledException" || name === "AbortException") return;
          console.error("[PdfPageViewer] Failed to render page " + pageNum + ":", err);
        });
      }).catch(function (err) {
        console.error("[PdfPageViewer] Failed to render page " + pageNum + ":", err);
        state.renderingPages[pageNum] = false;
      });
    }

    function getVisiblePages() {
      var scrollTop = scrollArea.scrollTop;
      var scrollBottom = scrollTop + scrollArea.clientHeight;
      var visible = [];

      for (var i = 0; i < state.pageWrappers.length; i++) {
        var w = state.pageWrappers[i];
        var top = w.offsetTop - scrollArea.offsetTop;
        var bottom = top + w.offsetHeight;
        if (bottom > scrollTop && top < scrollBottom) {
          visible.push(i + 1);
        }
      }
      return visible;
    }

    function renderVisiblePages() {
      var visible = getVisiblePages(); // 1-based display positions
      if (visible.length === 0) return;

      var minPos = Math.max(1, visible[0] - RENDER_BUFFER);
      var maxPos = Math.min(displayCount(), visible[visible.length - 1] + RENDER_BUFFER);

      for (var pos = minPos; pos <= maxPos; pos++) {
        renderPage(realPageAt(pos - 1));
      }

      // Update current page indicator based on topmost visible page
      var topDisplayIdx = visible[0] - 1;
      if (topDisplayIdx !== state.currentDisplayIdx) {
        state.currentDisplayIdx = topDisplayIdx;
        state.currentPage = realPageAt(topDisplayIdx);
        pageInput.value = state.currentPage;
      }
    }

    function rerenderAll() {
      updateZoomLabel();
      state.renderedPages = {};
      state.renderingPages = {};
      createPagePlaceholders();

      // Set placeholder heights based on raw viewport dimensions
      var promises = [];
      for (var i = 0; i < displayCount(); i++) {
        (function (idx) {
          promises.push(
            state.pdfDoc.getPage(realPageAt(idx)).then(function (page) {
              var rawVp = page.getViewport({ scale: 1 });
              var baseScale = getBaseScale(rawVp.width);
              var finalScale = baseScale * state.userZoom;
              var viewport = page.getViewport({ scale: finalScale });
              state.pageWrappers[idx].style.minHeight = viewport.height + "px";
            })
          );
        })(i);
      }

      Promise.all(promises).then(function () {
        renderVisiblePages();
        // Scroll to current page
        if (state.currentDisplayIdx > 0) {
          var wrapper = state.pageWrappers[state.currentDisplayIdx];
          if (wrapper) wrapper.scrollIntoView({ block: "start" });
        }
      });
    }

    // --- Event handlers ---

    var scrollTimeout = null;
    scrollArea.addEventListener("scroll", function () {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function () {
        renderVisiblePages();
      }, 100);
    });

    // Zoom with Ctrl+scroll
    scrollArea.addEventListener("wheel", function (e) {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        var delta = e.deltaY < 0 ? 0.1 : -0.1;
        adjustZoom(delta);
      }
    }, { passive: false });

    // Zoom with Ctrl+Plus/Minus
    containerEl.addEventListener("keydown", function (e) {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          adjustZoom(0.15);
        } else if (e.key === "-") {
          e.preventDefault();
          adjustZoom(-0.15);
        } else if (e.key === "0") {
          e.preventDefault();
          state.userZoom = 1.0;
          rerenderAll();
        }
      }
    });

    // Make container focusable for keyboard events
    if (!containerEl.getAttribute("tabindex")) {
      containerEl.setAttribute("tabindex", "0");
      containerEl.style.outline = "none";
    }

    // --- Load PDF ---

    var ready = loadPdfDocument(pdfUrl, { cacheKey: state.cacheKey }).then(function (pdf) {
      state.pdfDoc = pdf;
      state.totalPages = pdf.numPages;

      // Drop allowlist entries beyond the document; fall back to all pages if none remain.
      if (state.pageList) {
        state.pageList = state.pageList.filter(function (n) { return n <= pdf.numPages; });
        if (state.pageList.length === 0) state.pageList = null;
      }

      pageInput.max = pdf.numPages;
      if (state.pageList) {
        pageTotal.textContent = "(" + displayCount() + " changed page" + (displayCount() > 1 ? "s" : "") + ")";
      } else {
        pageTotal.textContent = "/ " + pdf.numPages;
      }

      // Resolve the starting display position: honor opts.page if it is shown, else first page.
      var startIdx = displayIdxOfReal(initialPage);
      if (startIdx < 0) startIdx = 0;
      state.currentDisplayIdx = startIdx;
      state.currentPage = realPageAt(startIdx);
      pageInput.value = state.currentPage;

      prevBtn.disabled = displayCount() <= 1;
      nextBtn.disabled = displayCount() <= 1;
      updateZoomLabel();

      // Set scroll area to a generous height so pages display fully
      scrollArea.style.maxHeight = "85vh";

      createPagePlaceholders();

      // Pre-compute placeholder heights then render visible
      rerenderAll();

      // Scroll to initial page if not the first shown page
      if (state.currentDisplayIdx > 0) {
        setTimeout(function () { goToDisplay(state.currentDisplayIdx); }, 200);
      }
    }).catch(function (err) {
      console.error("[PdfPageViewer] Failed to load PDF:", err && err.name, err && err.message, err);
      scrollArea.innerHTML =
        '<div style="text-align:center; padding:60px 20px; color:#dc3545;">' +
        '<i class="fas fa-exclamation-triangle fa-2x"></i>' +
        '<div style="margin-top:12px;">Failed to load PDF</div>' +
        '<button class="btn btn-sm btn-primary mt-2" onclick="window.location.reload()">Retry</button>' +
        '</div>';
      throw err;
    });

    // --- Public API ---
    return {
      goToPage: goToPage,
      getState: function () { return state; },
      adjustZoom: adjustZoom,
      ready: ready,
    };
  }

  return { init: init };
})();
